/**
 * Scoring which agent kind and model an 'auto' mission runs with (ADR 0049 decision 5, step 3),
 * from reported data only: what the machine's agents report (capabilities, sign-in, advertised
 * models), what finished missions cost and how they ended, and the project's policy. No vendor or
 * model is named here. Pure and browser-safe: the scheduler runs it to place, the console can run
 * it to explain.
 *
 *   score = preference (a strong prior, 1.5 for the first preferred kind, a quarter less for
 *           each next one, at least 0.75; it wins unless the evidence clearly says otherwise)
 *         + success on this project (finished missions done, Laplace-smoothed: no history is 0.5;
 *           kept per setup, kind + CLI version + model + run mode, so a new setup starts unknown;
 *           each outcome weighs half per 7 days and drops after 60; R-PL-6, 30 Oct 2026)
 *         + explore (R-PL-7: an unknown setup beside a known one wins a drawn share of small
 *           missions, 1 in 10 doubling per success up to 1 in 2, and its reason says so)
 *         + cost (learned per kind from finished missions, the cheapest candidate 1 and the
 *           dearest 0; unknown is 0.5, never 0 and never best)
 *         + fit (0, minus 1 when the agent was not signed in at its last start on the machine;
 *           whether a kind may run at all, approvals included, is policy's call before scoring:
 *           policyOffered)
 *         - spare (SPARE_PENALTY when the owner saves this kind's credits, SchedulerSettings
 *           agents.spare, and the work is not large: it runs only when no other kind can, or for
 *           large work; 20 Oct 2026)
 *         - capacity (0 under 70 percent of the kind's binding subscription window on this
 *           machine, rising to SPARE_PENALTY at 100 percent when it resets more than 6 hours out,
 *           lighter when it resets sooner: agent-usage.ts usagePressure; 20 Oct 2026)
 *
 * With no history every part is equal, so the order is the hub preference, then the machine's own. The model: the mission's own model when it applies to the
 * kind (the kind it was chosen for, or a kind that advertises it); else, for small work, the
 * cheapest advertised model finished missions learned a cost for; else the agent's own default.
 * Every pick carries one reason line built from the facts that decided it.
 */
import type { Id, Machine } from '../index.ts';
import { DEFAULT_AGENT_KIND, agentKindLabel } from './adapters.ts';
import { advertisedValues } from './agent-kinds.ts';
import type { Mission } from './missions.ts';
import type { Ts } from '../index.ts';
import { usagePressure } from './agent-usage.ts';
import type { AgentUsage } from './agent-usage.ts';

/** One finished mission, as scoring learns from it. */
export interface AgentOutcome {
  kind: string;
  model: string | null;
  projectId: Id;
  /** Closed as done. */
  ok: boolean;
  /** USD the mission's sessions cost, or null when unknown. */
  costUsd: number | null;
  /** 'reported': the agent's own ACP usage_update cost. 'priced': Orgus priced its tokens. */
  costFrom: 'reported' | 'priced' | null;
  /** When it closed: evidence weighs half per OUTCOME_HALF_LIFE_MS and drops after OUTCOME_MAX_AGE_MS (R-PL-6). Absent: weight 1. */
  at?: Ts;
  /** The CLI version it ran (MissionLaunch.agentVersion), part of its setup. */
  version?: string | null;
  /** How it ran, part of its setup; absent: headless. */
  mode?: 'headless' | 'terminal';
}

/** ADR 0087 decision 3 (R-PL-6): an outcome weighs half per 7 days and is dropped after 60. */
export const OUTCOME_HALF_LIFE_MS = 7 * 86_400_000;
export const OUTCOME_MAX_AGE_MS = 60 * 86_400_000;

/** How much an outcome still counts at `now`: 1 when new, half a week on, 0 past 60 days. */
export function outcomeWeight(o: Pick<AgentOutcome, 'at'>, now: Ts): number {
  if (o.at == null) return 1;
  const age = Math.max(0, now - o.at);
  return age > OUTCOME_MAX_AGE_MS ? 0 : 0.5 ** (age / OUTCOME_HALF_LIFE_MS);
}

/** One setup an agent runs as (R-PL-6): evidence is kept per setup, and a new one starts unknown. */
export interface ScoredSetup {
  kind: string;
  version: string | null;
  model: string | null;
  mode: 'headless' | 'terminal';
}

export function sameSetup(o: AgentOutcome, s: ScoredSetup): boolean {
  return o.kind === s.kind && (o.version ?? null) === s.version && (o.model ?? null) === s.model && (o.mode ?? 'headless') === s.mode;
}

/** A setup with fewer outcomes than this is still explored (R-PL-7). */
export const EXPLORE_UNTIL = 4;
/** An explored setup wins its drawn share of small missions by this much. */
const EXPLORE_BONUS = 10;

/** R-PL-7: the share of small missions an unknown setup gets: 1 in 10, doubling per success, at most 1 in 2. */
export function exploreShare(successes: number): number {
  return Math.min(0.5, 0.1 * 2 ** Math.max(0, successes));
}

/** A stable draw in [0, 1) for one queue entry: whether its small mission explores. */
export function exploreDraw(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return (h >>> 0) / 2 ** 32;
}

/** How big the work is, from the item's estimate (points): small at 2 or less, large at 5 or more. */
export type WorkSize = 'small' | 'large' | null;

export function workSizeOf(estimate: number | null | undefined): WorkSize {
  if (estimate == null || !Number.isFinite(estimate) || estimate <= 0) return null;
  return estimate <= 2 ? 'small' : estimate >= 5 ? 'large' : null;
}

export interface AgentScoreInput {
  machine: Pick<Machine, 'runtimes'>;
  /** Kinds the machine offers that policy allows, in the machine's order. */
  offered: readonly string[];
  projectId: Id;
  /** The hub's preferred kinds (its scheduler setting), first is strongest. */
  preferred: readonly string[];
  outcomes: readonly AgentOutcome[];
  /** The mission's model (chosen for Claude Code), if any. */
  requestedModel?: string | null;
  size?: WorkSize;
  /** Kinds only the ACP Registry knows. */
  acpKinds?: readonly string[];
  /** Kinds whose credits the owner saves (SchedulerSettings.agents.spare): last, except for large work. */
  spare?: readonly string[];
  /**
   * The machine's latest subscription usage per kind (agent-usage.ts): a kind near a limit that
   * resets late scores lower (`capacity`). Additive, 20 Oct 2026.
   */
  usage?: readonly AgentUsage[];
  /** For the usage windows (reset times); default Date.now(). */
  now?: Ts;
  /** The hub clock's UTC offset for reset times in reasons (SchedulerSettings.utcOffsetMinutes). */
  utcOffsetMinutes?: number | null;
  /** How the start runs (part of the setup); default headless. */
  mode?: 'headless' | 'terminal';
  /** This entry's draw in [0, 1) (exploreDraw): small work below an unknown setup's share explores it (R-PL-7). Absent: no exploring. */
  draw?: number;
}

export interface AgentScoreParts {
  preference: number;
  success: number;
  cost: number;
  fit: number;
  /** 0, or minus SPARE_PENALTY for a kind whose credits are saved, on work that is not large. */
  spare?: number;
  /** 0, or down to minus SPARE_PENALTY as the kind's binding usage window fills (usagePressure). */
  capacity?: number;
  /** EXPLORE_BONUS when this entry's draw explores this unknown setup (R-PL-7). */
  explore?: number;
}

/** More than preference, success and cost together can add (1.5 + 1 + 1): a saved kind goes last. */
export const SPARE_PENALTY = 4;

/** Is this kind's credit saved for this work (agents.spare, and the work is not large)? */
export function sparedFor(kind: string, spare: readonly string[] | undefined, size: WorkSize | undefined): boolean {
  return !!spare?.includes(kind) && size !== 'large';
}

/**
 * The preference order with saved kinds last (sparedFor), for the paths that pick without
 * scoring: preferred kinds that are not saved, then the machine's other kinds that are not.
 * Saved kinds are left out, so the machine's order puts them after every other kind.
 */
export function spareOrder(preferred: readonly string[], offered: readonly string[], spare: readonly string[] | undefined, size?: WorkSize): string[] {
  if (!spare?.length || size === 'large') return [...preferred];
  const keep = (k: string) => !sparedFor(k, spare, size);
  return [...new Set([...preferred.filter(keep), ...offered.filter(keep)])];
}

export interface AgentPick {
  kind: string;
  /** The model to start with; null: the agent's own default. */
  model: string | null;
  score: number;
  parts: AgentScoreParts;
  /** One plain line: why this kind (and model) was picked. */
  reason: string;
}

/** Learning keeps at most this many outcomes (newest first). */
export const AGENT_OUTCOMES_MAX = 500;

const mean = (xs: number[]): number | null => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
const usd = (x: number) => `$${x < 0.1 ? x.toFixed(3) : x.toFixed(2)}`;

/** Average known cost per finished mission of a kind (and model), and where the numbers came from. */
function costOf(outcomes: readonly AgentOutcome[], kind: string, model?: string | null): { avg: number | null; reported: boolean } {
  const known = outcomes.filter((o) => o.kind === kind && (model === undefined || o.model === model) && o.costUsd !== null && o.costUsd >= 0);
  return { avg: mean(known.map((o) => o.costUsd as number)), reported: known.some((o) => o.costFrom === 'reported') };
}

/**
 * Every offered kind scored, best first (ties in the machine's order). Empty when nothing is
 * offered. See the module comment for the formula.
 */
export function scoreAgents(input: AgentScoreInput): AgentPick[] {
  const { machine, offered, projectId, preferred, outcomes } = input;
  const costs = new Map(offered.map((k) => [k, costOf(outcomes, k)]));
  const known = [...costs.values()].map((c) => c.avg).filter((x): x is number => x !== null);
  const lo = known.length ? Math.min(...known) : 0;
  const hi = known.length ? Math.max(...known) : 0;
  const now = input.now ?? Date.now();
  // R-PL-6: evidence per setup (kind, version, model, run mode), each outcome weighed by its age.
  const setups = new Map(
    offered.map((kind) => {
      const m = modelFor(input, kind);
      const setup: ScoredSetup = { kind, version: machine.runtimes?.find((r) => r.kind === kind)?.version ?? null, model: m.model, mode: input.mode ?? 'headless' };
      const live = outcomes.map((o) => ({ o, w: outcomeWeight(o, now) })).filter((x) => x.w > 0 && sameSetup(x.o, setup));
      return [kind, { ...m, setup, live }] as const;
    }),
  );
  const anyKnown = [...setups.values()].some((s) => s.live.length >= EXPLORE_UNTIL);
  const picks = offered.map((kind, order) => {
    const pi = preferred.indexOf(kind);
    const preference = pi < 0 ? 0 : Math.max(0.75, 1.5 - pi * 0.25);
    const { model, why: modelWhy, setup, live } = setups.get(kind)!;
    const here = live.filter((x) => x.o.projectId === projectId);
    const done = here.filter((x) => x.o.ok).length;
    const wAll = here.reduce((a, x) => a + x.w, 0);
    const wDone = here.reduce((a, x) => a + (x.o.ok ? x.w : 0), 0);
    const success = (wDone + 1) / (wAll + 2);
    const c = costs.get(kind)!;
    const cost = c.avg === null || hi === lo ? 0.5 : 1 - (c.avg - lo) / (hi - lo);
    const summary = machine.runtimes?.find((r) => r.kind === kind)?.acp;
    const notSignedIn = summary?.signedIn === false;
    const fit = notSignedIn ? -1 : 0;
    const spared = sparedFor(kind, input.spare, input.size);
    // R-PL-7: an unknown setup, beside one that is known, takes its share of small missions.
    const share = exploreShare(live.filter((x) => x.o.ok).length);
    const unknown = live.length < EXPLORE_UNTIL;
    const explore = unknown && anyKnown && input.size === 'small' && input.draw !== undefined && input.draw < share && !spared && !notSignedIn ? EXPLORE_BONUS : 0;
    const pressure = usagePressure(input.usage?.find((u) => u.agentKind === kind), input.now ?? Date.now(), input.utcOffsetMinutes ?? null);
    const capacity = pressure.weight > 0 ? -Math.round(SPARE_PENALTY * pressure.weight * 100) / 100 : 0;
    const facts: string[] = [];
    if (explore) facts.push(`exploring ${agentKindLabel(kind)}${setup.version ? ` ${setup.version}` : ''}: 1 in ${Math.round(1 / share)} small missions`);
    if (pressure.text) facts.push(pressure.text);
    if (spared) facts.push('saving its credits for large work');
    else if (input.spare?.includes(kind)) facts.push('large work, where its saved credits go');
    if (pi >= 0) facts.push(pi === 0 ? 'tried first on this hub' : `choice ${pi + 1} on this hub`);
    if (here.length) facts.push(`${done} of ${here.length} missions done on this project`);
    if (c.avg !== null) facts.push(`about ${usd(c.avg)} a mission${c.reported ? ', as the agent reported' : ''}`);
    else if (known.length) facts.push('cost not learned yet');
    if (notSignedIn) facts.push('not signed in at its last start');
    if (modelWhy) facts.push(modelWhy);
    const text = facts.length ? facts.join('; ') : order === 0 ? 'first working agent on this machine, no history yet' : 'no history yet';
    const score = preference + success + cost + fit - (spared ? SPARE_PENALTY : 0) + capacity + explore;
    return { pick: { kind, model, score, parts: { preference, success, cost, fit, ...(spared ? { spare: -SPARE_PENALTY } : {}), ...(capacity ? { capacity } : {}), ...(explore ? { explore } : {}) }, reason: text.charAt(0).toUpperCase() + text.slice(1) }, order };
  });
  return picks.sort((a, b) => b.pick.score - a.pick.score || a.order - b.order).map((p) => p.pick);
}

/** The model a kind starts with, and why when it is not the obvious one. */
function modelFor(input: AgentScoreInput, kind: string): { model: string | null; why: string | null } {
  const req = input.requestedModel?.trim() || null;
  const advertised = advertisedValues(input.machine.runtimes?.find((r) => r.kind === kind)?.acp, 'model');
  if (req && (kind === DEFAULT_AGENT_KIND || advertised.includes(req))) return { model: req, why: null };
  if (input.size !== 'small' || advertised.length < 2) return { model: null, why: null };
  let best: { model: string; avg: number } | null = null;
  for (const m of advertised) {
    const c = costOf(input.outcomes, kind, m).avg;
    if (c !== null && (!best || c < best.avg)) best = { model: m, avg: c };
  }
  return best ? { model: best.model, why: `${best.model} for small work, its cheapest learned model` } : { model: null, why: null };
}

/**
 * What one closed mission teaches: the kind and model of its last start that reached a machine,
 * whether it closed as done, and its cost (the agent's own reported cost when every session
 * reported one in USD, else what Orgus priced, else unknown). null for an open mission or one
 * that never started.
 *
 * Only the task's outcome teaches (20 Oct 2026): a mission stopped after its agent failed for a
 * reason of its own (`agentFault` of that start's session: signed out, a usage limit, a crash,
 * its sandbox) is not that agent's failure, so it is null. Nor is a landing setback (R-PL-6, 30 Oct
 * 2026): a mission stopped while Orgus could not land its work (a conflict with main, a refused or
 * failed landing) teaches nothing; failed checks are the task's and count.
 */
export function outcomeOf(
  m: Pick<Mission, 'projectId' | 'closed' | 'launches' | 'sessionIds'> & { integration?: { status: string } | null },
  reportedUsd: (sessionId: Id) => number | null,
  pricedUsd: (sessionId: Id) => number | null,
  agentFault?: (sessionId: Id) => boolean,
): AgentOutcome | null {
  if (!m.closed) return null;
  const last = [...m.launches].reverse().find((l) => l.result === 'ok');
  if (!last) return null;
  if (m.closed.as !== 'done' && last.sessionId && agentFault?.(last.sessionId)) return null;
  if (m.closed.as !== 'done' && m.integration && LANDING_SETBACKS.has(m.integration.status)) return null;
  const sessions = m.sessionIds.length ? m.sessionIds : last.sessionId ? [last.sessionId] : [];
  const reported = sessions.map(reportedUsd);
  const priced = sessions.map(pricedUsd);
  const all = (xs: (number | null)[]) => (xs.length && xs.every((x) => x !== null) ? (xs as number[]).reduce((a, b) => a + b, 0) : null);
  const r = all(reported);
  const p = r === null ? all(priced) : null;
  return {
    kind: last.agentKind ?? DEFAULT_AGENT_KIND,
    model: last.model,
    projectId: m.projectId,
    ok: m.closed.as === 'done',
    costUsd: r ?? p,
    costFrom: r !== null ? 'reported' : p !== null ? 'priced' : null,
    at: m.closed.at,
    version: last.agentVersion ?? null,
    mode: last.mode,
  };
}

/** Integration states that are Orgus's landing, not the agent's task (MissionIntegration.status). */
const LANDING_SETBACKS = new Set(['conflict', 'lost', 'land-failed', 'ff-refused', 'refused', 'error', 'unsupported', 'stopping', 'running']);
